import { AfterViewInit, Directive, ElementRef, Input, NgZone, OnDestroy, inject } from '@angular/core';
import { UserLoginService } from 'src/app/Common/UserLoginService/userlogin.service';

/**
 * Drag-to-resize columns for any <table> with a <thead>.
 *
 * Usage:  <table appResizableColumns> ... </table>
 *
 * - The default layout is left untouched until the user drags a column edge.
 * - On the first drag, every column's current rendered width is frozen in px so
 *   only the dragged column changes; the table grows/shrinks by the same amount
 *   (the wrapping .table-responsive provides the horizontal scroll).
 * - Double-click a column edge to restore the default layout.
 * - If header columns are added/removed later (e.g. *ngIf'd Discount/Tax), the
 *   new headers get handles and the layout resets to default (then re-applies any
 *   saved widths).
 *
 * Persistence (optional): give the table a unique key and the widths are saved per
 * logged-in user in localStorage, and re-applied on the next load / page refresh:
 *   <table appResizableColumns resizeStorageKey="material-inward-items">
 * Columns are matched by the header's data-col-key, else its title, else its text,
 * so saved widths survive columns being shown/hidden. Double-click clears the save.
 */
@Directive({
  selector: 'table[appResizableColumns]'
})
export class ResizableColumnsDirective implements AfterViewInit, OnDestroy {
  @Input() minColWidth = 40;
  /** Unique key for this grid. When set, column widths are remembered per user. */
  @Input() resizeStorageKey?: string;

  private userLoginService = inject(UserLoginService);

  private table!: HTMLTableElement;
  private frozen = false;
  /** Inline styles from the template (e.g. style="width: 4%") so reset() can restore them exactly. */
  private originalThWidths = new Map<HTMLTableCellElement, string>();
  private originalTable = { width: '', minWidth: '', tableLayout: '' };
  private handles = new Map<HTMLTableCellElement, () => void>();
  /** Rendered width minus CSS width per header (collapsed borders), so set widths render exactly. */
  private offsets = new Map<HTMLTableCellElement, number>();
  private observer?: MutationObserver;

  constructor(private el: ElementRef<HTMLTableElement>, private zone: NgZone) {}

  ngAfterViewInit(): void {
    this.table = this.el.nativeElement;
    // Outside Angular so dragging doesn't trigger change detection on every pointer move.
    this.zone.runOutsideAngular(() => {
      this.attachHandles();
      // Wait one frame so the modal/off-canvas has its final width before measuring.
      requestAnimationFrame(() => this.restoreSaved());
      const thead = this.table.tHead;
      if (thead) {
        this.observer = new MutationObserver(() => this.onHeaderChanged());
        this.observer.observe(thead, { childList: true, subtree: true });
      }
    });
  }

  ngOnDestroy(): void {
    this.observer?.disconnect();
    this.handles.forEach(remove => remove());
    this.handles.clear();
    document.body.classList.remove('col-resizing');
  }

  private headerCells(): HTMLTableCellElement[] {
    const thead = this.table.tHead;
    if (!thead || !thead.rows.length) return [];
    return Array.from(thead.rows[thead.rows.length - 1].cells);
  }

  private onHeaderChanged(): void {
    const current = this.headerCells();
    const changed = current.length !== this.handles.size || current.some(th => !this.handles.has(th));
    if (!changed) return; // mutation was just our own handle insertion

    // Drop handles of headers that no longer exist.
    this.handles.forEach((remove, th) => {
      if (!current.includes(th)) { remove(); this.handles.delete(th); }
    });
    this.attachHandles();
    if (this.frozen) this.reset(false);
    requestAnimationFrame(() => this.restoreSaved());
  }

  private attachHandles(): void {
    this.headerCells().forEach(th => {
      if (this.handles.has(th)) return;

      th.classList.add('col-resizable');
      const handle = document.createElement('span');
      handle.className = 'col-resize-handle';
      handle.title = 'Drag to resize, double-click to reset';

      const onDown = (e: PointerEvent) => this.startDrag(e, th, handle);
      const onDbl = (e: MouseEvent) => { e.preventDefault(); e.stopPropagation(); this.reset(); };
      const onClick = (e: MouseEvent) => { e.preventDefault(); e.stopPropagation(); }; // don't toggle select-all / sort

      handle.addEventListener('pointerdown', onDown);
      handle.addEventListener('dblclick', onDbl);
      handle.addEventListener('click', onClick);
      th.appendChild(handle);

      this.handles.set(th, () => {
        handle.removeEventListener('pointerdown', onDown);
        handle.removeEventListener('dblclick', onDbl);
        handle.removeEventListener('click', onClick);
        handle.remove();
        th.classList.remove('col-resizable');
      });
    });
  }

  /** Lock every column at its current rendered width so only the dragged column changes. */
  private freeze(): void {
    if (this.frozen) return;
    const ths = this.headerCells();
    const widths = ths.map(th => th.getBoundingClientRect().width);
    const tableWidth = this.table.getBoundingClientRect().width;

    this.originalThWidths.clear();
    ths.forEach(th => this.originalThWidths.set(th, th.style.width));
    this.originalTable = {
      width: this.table.style.width,
      minWidth: this.table.style.minWidth,
      tableLayout: this.table.style.tableLayout
    };

    ths.forEach((th, i) => { if (widths[i] > 0) th.style.width = `${widths[i]}px`; });
    this.table.style.width = `${tableWidth}px`;
    this.table.style.minWidth = '0';
    this.table.style.tableLayout = 'fixed';

    // Measure how much each header renders wider than its CSS width, then correct for it
    // so columns don't shift by a border's width when frozen (or on every reload).
    this.offsets.clear();
    ths.forEach((th, i) => {
      if (widths[i] <= 0) return;
      const offset = th.getBoundingClientRect().width - widths[i];
      this.offsets.set(th, offset);
      th.style.width = `${widths[i] - offset}px`;
    });
    this.table.style.width = `${tableWidth}px`;
    this.frozen = true;
  }

  private startDrag(e: PointerEvent, th: HTMLTableCellElement, handle: HTMLElement): void {
    if (e.button !== 0) return;
    e.preventDefault();
    e.stopPropagation();

    this.freeze();

    const startX = e.clientX;
    const startCol = th.getBoundingClientRect().width;
    const startTable = this.table.getBoundingClientRect().width;

    handle.setPointerCapture(e.pointerId);
    document.body.classList.add('col-resizing');

    const move = (ev: PointerEvent) => {
      const w = Math.max(this.minColWidth, startCol + (ev.clientX - startX));
      this.setWidth(th, w);
      this.table.style.width = `${startTable + (w - startCol)}px`;
    };

    const up = (ev: PointerEvent) => {
      if (handle.hasPointerCapture(ev.pointerId)) handle.releasePointerCapture(ev.pointerId);
      handle.removeEventListener('pointermove', move);
      handle.removeEventListener('pointerup', up);
      handle.removeEventListener('pointercancel', up);
      document.body.classList.remove('col-resizing');
      this.save();
    };

    handle.addEventListener('pointermove', move);
    handle.addEventListener('pointerup', up);
    handle.addEventListener('pointercancel', up);
  }

  /** Restore the original (default) layout from the template/CSS. */
  reset(clearSaved = true): void {
    if (clearSaved) this.clearSaved();
    if (!this.frozen) return;
    // Only touch headers we froze — headers added later keep their own template width.
    this.originalThWidths.forEach((w, th) => (th.style.width = w));
    this.table.style.width = this.originalTable.width;
    this.table.style.minWidth = this.originalTable.minWidth;
    this.table.style.tableLayout = this.originalTable.tableLayout;
    this.originalThWidths.clear();
    this.offsets.clear();
    this.frozen = false;
  }

  /** Set a header's rendered width in px. */
  private setWidth(th: HTMLTableCellElement, px: number): void {
    th.style.width = `${px - (this.offsets.get(th) ?? 0)}px`;
  }

  // ---------------------------------------------------------------------------
  // Persistence
  // ---------------------------------------------------------------------------

  private get storageKey(): string | null {
    if (!this.resizeStorageKey) return null;
    let userId: number | string = 'anon';
    try { userId = this.userLoginService.getCurrentUser()?.userId ?? 'anon'; } catch { /* ignore */ }
    return `advColWidths:${userId}:${this.resizeStorageKey}`;
  }

  /** Stable identifier for a header, independent of column position. */
  private colKey(th: HTMLTableCellElement, index: number): string {
    const explicit = th.dataset['colKey'] || th.getAttribute('title');
    if (explicit) return explicit.trim();
    const text = Array.from(th.childNodes)
      .filter(n => !(n instanceof HTMLElement && n.classList.contains('col-resize-handle')))
      .map(n => n.textContent || '')
      .join(' ')
      .replace(/\s+/g, ' ')
      .trim();
    return text || `#${index}`;
  }

  private save(): void {
    const key = this.storageKey;
    if (!key) return;
    const widths: Record<string, number> = {};
    this.headerCells().forEach((th, i) => {
      const w = th.getBoundingClientRect().width;
      if (w > 0) widths[this.colKey(th, i)] = Math.round(w);
    });
    try {
      // Merge so widths of columns currently hidden (e.g. Discount/Tax off) are kept.
      const existing = JSON.parse(localStorage.getItem(key) || '{}');
      localStorage.setItem(key, JSON.stringify({ ...existing, ...widths }));
    } catch { /* storage full / blocked - resizing still works, just not remembered */ }
  }

  private clearSaved(): void {
    const key = this.storageKey;
    if (!key) return;
    try { localStorage.removeItem(key); } catch { /* ignore */ }
  }

  private restoreSaved(): void {
    const key = this.storageKey;
    if (!key || !this.table?.isConnected) return;

    let saved: Record<string, number> | null = null;
    try { saved = JSON.parse(localStorage.getItem(key) || 'null'); } catch { saved = null; }
    if (!saved || typeof saved !== 'object') return;

    const ths = this.headerCells();
    const matched = ths.some((th, i) => typeof saved![this.colKey(th, i)] === 'number');
    if (!matched) return;

    this.freeze();
    let total = 0;
    ths.forEach((th, i) => {
      const current = th.getBoundingClientRect().width;
      if (current <= 0) return; // hidden by a responsive rule
      const w = saved![this.colKey(th, i)];
      const width = typeof w === 'number' ? Math.max(this.minColWidth, w) : current;
      this.setWidth(th, width);
      total += width;
    });
    if (total > 0) this.table.style.width = `${total}px`;
  }
}
